/** Query param carrying the in-app path to open after a successful login. */
export const POST_LOGIN_REDIRECT_PARAM = 'redirect' as const

/**
 * Query param carrying the action a signed-out visitor was blocked from taking.
 * It rides on the return path, so the whole thing is still one `redirect=`
 * value and the hardening in `getSafePostLoginPath` covers it unchanged.
 *
 *   /login?redirect=%2Fvalidate-ideas%2FX%3Fintent%3Dvalidate
 *
 * The destination page reads it on mount, opens or focuses the matching UI,
 * then strips it from the URL. Nothing is ever submitted automatically: the
 * user still presses the button themselves, so a refresh or a back-button can
 * never double-post or double-vote.
 */
export const AUTH_INTENT_PARAM = 'intent' as const

export const AUTH_INTENTS = [
  'validate',
  'comment',
  'vote-up',
  'vote-down',
  'like-comment',
  'submit-idea',
] as const

export type AuthIntent = (typeof AUTH_INTENTS)[number]

export function isAuthIntent(
  raw: string | null | undefined,
): raw is AuthIntent {
  return raw != null && (AUTH_INTENTS as readonly string[]).includes(raw)
}

/**
 * Returns a safe internal path for client-side navigation, or null if the value
 * must not be used (open-redirect hardening).
 */
export function getSafePostLoginPath(
  raw: string | null | undefined,
): string | null {
  if (raw == null || raw === '') return null
  let decoded: string
  try {
    decoded = decodeURIComponent(raw.trim())
  } catch {
    return null
  }
  if (!decoded.startsWith('/') || decoded.startsWith('//')) return null
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(decoded) || decoded.includes('://')) {
    return null
  }
  if (decoded.includes('\\')) return null
  const pathOnly = decoded.split(/[?#]/)[0]
  if (pathOnly === '/login') return null
  return decoded
}

/**
 * True for the sign-in and sign-up routes. A page that guards navigation away
 * from unsaved work uses this to let sign-in through, since the visitor is
 * being sent there to come straight back.
 */
export function isAuthRoutePath(pathname: string): boolean {
  return pathname === '/login' || pathname === '/signup'
}

/** Appends `?intent=…` to an in-app path, replacing any intent already on it. */
export function withAuthIntent(
  path: string,
  intent?: AuthIntent | null,
): string {
  if (!intent) return path
  const [base, hash = ''] = path.split('#')
  const [pathname, search = ''] = base.split('?')
  const params = new URLSearchParams(search)
  params.set(AUTH_INTENT_PARAM, intent)
  return `${pathname}?${params.toString()}${hash ? `#${hash}` : ''}`
}

/** Strips the intent param, so a reload of the landed page is a plain view. */
export function withoutAuthIntent(search: string): string {
  const params = new URLSearchParams(search)
  params.delete(AUTH_INTENT_PARAM)
  const rest = params.toString()
  return rest ? `?${rest}` : ''
}

function buildAuthUrl(
  base: '/login' | '/signup',
  returnTo: string,
  intent?: AuthIntent | null,
): string {
  const params = new URLSearchParams()
  params.set(POST_LOGIN_REDIRECT_PARAM, withAuthIntent(returnTo, intent))
  return `${base}?${params.toString()}`
}

export function buildLoginUrlWithRedirect(
  pathname: string,
  search: string,
  hash: string,
  intent?: AuthIntent | null,
): string {
  return buildAuthUrl(`/login`, `${pathname}${search}${hash}`, intent)
}

export function buildSignupUrlWithRedirect(
  pathname: string,
  search: string,
  hash: string,
  intent?: AuthIntent | null,
): string {
  return buildAuthUrl(`/signup`, `${pathname}${search}${hash}`, intent)
}
